import React, { useContext, useEffect, useState } from 'react';

// OneTrust cookie categories: C0002 = Performance, C0004 = Targeting
export const YOUTUBE_REQUIRED_GROUPS = ['C0002', 'C0004'];

declare global {
	interface Window {
		OnetrustActiveGroups?: string;
		OptanonWrapper?: () => void;
	}
}

const ConsentContext = React.createContext<string[]>([]);

// OnetrustActiveGroups is a comma separated string, e.g. ",C0001,C0002,"
const getActiveGroups = (): string[] => (window.OnetrustActiveGroups ?? '').split(',').filter(Boolean);

export interface ConsentContextProviderProps {
	children?: React.ReactNode;
}

export const ConsentContextProvider: React.FC<ConsentContextProviderProps> = ({ children }) => {
	// Empty until mounted, so server and first client render both treat consent as not given
	const [activeGroups, setActiveGroups] = useState<string[]>([]);

	useEffect(() => {
		const updateActiveGroups = () => setActiveGroups(getActiveGroups());
		updateActiveGroups();

		window.addEventListener('OneTrustGroupsUpdated', updateActiveGroups);

		// OptanonWrapper may already be set by GTM, so call the previous one before ours
		const previousOptanonWrapper = window.OptanonWrapper;
		window.OptanonWrapper = () => {
			previousOptanonWrapper?.();
			updateActiveGroups();
		};

		return () => {
			window.removeEventListener('OneTrustGroupsUpdated', updateActiveGroups);
			window.OptanonWrapper = previousOptanonWrapper;
		};
	}, []);

	return <ConsentContext.Provider value={activeGroups}>{children}</ConsentContext.Provider>;
};

export function useConsent(requiredGroups: string[]): boolean {
	const activeGroups = useContext(ConsentContext);
	return requiredGroups.every((group) => activeGroups.includes(group));
}
